import {type Variables, type GraphQLResponse, stableCopy} from 'relay-runtime'
import {getInsightsUrl, reportTraceData} from '@github-ui/api-insights'
import {verifiedFetch} from '@github-ui/verified-fetch'

type Json = string | number | boolean | null | {[property: string]: Json} | Json[]

type JsonRoot = {[property: string]: Json}

export type GraphQLError = {type: string; message: string; path: string}
type GraphQLSuccessfulResult = {data: JsonRoot; timestamp?: number}
type GraphQLErrorResult = {errors: GraphQLError[]; data?: JsonRoot; timestamp?: number}

// "SERVICE_UNAVAILABLE" are availability issues. It is allowed as we expect the app code to handle it
const ALLOWED_ERRORS = ['SAML', 'SERVICE_UNAVAILABLE']

export const IssuesShowRegex = new RegExp(/^\/[\w-_]*\/[\w-_]*\/issues\/\d*$/)

export type GraphQLResult = GraphQLSuccessfulResult | GraphQLErrorResult
export type GraphQLSubscriptionResult = {subscriptionId: string | null; response: GraphQLResponse}

function validateNoErrors(
  decoded: GraphQLResult,
  requestId: string,
  persistedQueryId: string,
): GraphQLSuccessfulResult {
  removeAllowedErrors(requestId, decoded)
  if ('errors' in decoded && decoded.errors.length) {
    const formatted = decoded.errors
      .map(error => `GraphQL error: ${error.type}: ${error.message} (path: ${error.path})`)
      .join(', ')
    throw new Error(`${formatted} (Persisted query id: ${persistedQueryId})`, {cause: decoded.errors})
  }
  if (!('data' in decoded)) {
    throw new Error(`Expected data property in response: ${JSON.stringify(decoded)}`)
  }
  return decoded as GraphQLSuccessfulResult
}

async function assertNoHttpErrors(response: Response): Promise<void> {
  // 404 is handled by GraphQL
  if (response.status >= 400 && response.status !== 404) {
    const text = await response.text()
    throw new Error(`HTTP error (${response.status}): ${text}`)
  }
}

function removeAllowedErrors(requestId: string, decoded: GraphQLResult): GraphQLResult {
  if ('errors' in decoded) {
    decoded.errors
      .filter(error => ALLOWED_ERRORS.includes(error.type))
      .map(error => {
        // eslint-disable-next-line no-console
        console.error(
          `Failed to fetch data. Please use this request ID when contacting support: ${requestId} Error: ${error.type}: ${error.message} (path: ${error.path})`,
        )
      })

    decoded.errors = decoded.errors.filter(error => !ALLOWED_ERRORS.includes(error.type))
  }
  return decoded
}

// Fetch GraphQL from the server and return a decoded result.
export default async function fetchGraphQL(
  persistedQueryId: string,
  variables: Variables,
  method: 'GET' | 'POST' = 'GET',
  baseUrl?: string,
  enabledFeatures?: {[key: string]: boolean},
): Promise<GraphQLResponse> {
  const result = await fetchGraphQLWithSubscription(
    persistedQueryId,
    variables,
    method,
    {
      isSubscription: false,
      scope: undefined,
    },
    baseUrl,
    enabledFeatures,
  )
  return result.response
}

// Fetch GraphQL from the server and return a response promise along with an
// optional subscriptionId if the query is a subscription.
export async function fetchGraphQLWithSubscription(
  persistedQueryId: string,
  variables: Variables,
  method: 'GET' | 'POST' = 'POST',
  options: {
    isSubscription?: boolean
    subscriptionTopic?: string
    dispatchTime?: number
    scopeObject?: Record<string, unknown>
    scope?: string
  } = {},
  baseUrl?: string,
  enabledFeatures?: {[key: string]: boolean},
): Promise<GraphQLSubscriptionResult> {
  const canonicalizedPayload = JSON.stringify(
    // stableCopy will alphabetize the keys in the variable payload.
    // Necessary to ensure a match against early-hinted/preloaded requests.
    stableCopy({
      query: persistedQueryId,
      variables,
      ...(options.scopeObject ? {scopeObject: options.scopeObject} : {}),
    }),
  )

  const {isSubscription, scope, subscriptionTopic, dispatchTime} = options

  const url = constructUrl(
    method,
    encodeURIComponent(canonicalizedPayload),
    isSubscription,
    subscriptionTopic,
    scope,
    dispatchTime,
    baseUrl,
  )

  const {subscriptionId, requestId, json} = await getGraphQLQuery(url, method, canonicalizedPayload, enabledFeatures)
  const cleaned = validateNoErrors(json as GraphQLResult, requestId, persistedQueryId)

  if (cleaned) {
    reportTraceData(cleaned)
  }

  return {subscriptionId, response: cleaned}
}

function constructUrl(
  method: 'GET' | 'POST',
  content: string,
  isSubscription?: boolean,
  subscriptionTopic?: string,
  scope?: string,
  dispatchTime?: number,
  baseUrl = '/_graphql',
) {
  const queryParameters = []
  if (method === 'GET') {
    queryParameters.push(`body=${content}`)
  }
  if (isSubscription) {
    queryParameters.push('subscription=1')
  }
  if (scope) {
    queryParameters.push(`scope=${encodeURIComponent(scope)}`)
  }
  if (subscriptionTopic) {
    queryParameters.push(`subscriptionTopic=${encodeURIComponent(subscriptionTopic)}`)
  }
  if (dispatchTime) {
    queryParameters.push(`dispatchTime=${encodeURIComponent(dispatchTime)}`)
  }

  return queryParameters.length > 0 ? `${baseUrl}?${queryParameters.join('&')}` : baseUrl
}

async function getGraphQLQuery(url: string, method: string, body?: string, enabledFeatures?: {[key: string]: boolean}) {
  const effectiveUrl = getInsightsUrl(url)
  return getGraphQLData(effectiveUrl, method, body, enabledFeatures)
}

async function getGraphQLData(url: string, method: string, body?: string, enabledFeatures?: {[key: string]: boolean}) {
  let httpResponse: Response

  const bodyInit = body ? {body} : undefined

  const headers: {[key: string]: string} = {}
  if (enabledFeatures?.issues_react_perf_test) {
    headers['X-LUC-Environment'] = 'issues'
  }

  if (method === 'GET') {
    httpResponse = await fetch(url, {
      method,
      cache: 'no-cache',
      credentials: 'include',
      headers,
    })
  } else {
    httpResponse = await verifiedFetch(url, {
      method,
      headers: {
        Accept: 'application/json',
        ...headers,
      },
      ...bodyInit,
    })
  }

  await assertNoHttpErrors(httpResponse)
  const json = await httpResponse.json()
  const subscriptionId = httpResponse.headers.get('X-Subscription-ID')
  const requestId = httpResponse.headers.get('X-Github-Request-Id') || ''

  return {subscriptionId, requestId, json}
}
